/**
 * The fields this ranking actually reads. Declared structurally so both the
 * full Medicine DTO and the slim MedicineSearchIndex row satisfy it.
 */
interface SearchableMedicine {
  name: string;
  shortName?: string | null;
  genericName?: string | null;
  composition?: string | null;
  batchNo: string;
}

/**
 * Ranks the cached catalogue against a typed query. Runs entirely in the
 * browser — the catalogue is already loaded, and a counter with a queue can't
 * wait on a network round trip per keystroke.
 */
export function searchMedicines<T extends SearchableMedicine>(catalogue: T[], query: string, limit = 20): T[] {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) return [];

  const scored = catalogue
    .map((medicine) => ({ medicine, score: scoreMedicine(medicine, normalized) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.medicine.name.localeCompare(b.medicine.name));

  return scored.slice(0, limit).map((entry) => entry.medicine);
}

function scoreMedicine(medicine: SearchableMedicine, query: string): number {
  const name = medicine.name.toLowerCase();
  const shortName = medicine.shortName?.toLowerCase() ?? '';
  const generic = medicine.genericName?.toLowerCase() ?? '';
  const composition = medicine.composition?.toLowerCase() ?? '';
  const batch = medicine.batchNo.toLowerCase();

  if (name === query || shortName === query) return 100;
  if (name.startsWith(query) || shortName.startsWith(query)) return 90;
  if (name.includes(query)) return 70;
  if (shortName.includes(query)) return 65;
  if (generic.includes(query) || composition.includes(query)) return 50;
  if (batch.includes(query)) return 30;
  return 0;
}
